import { useEffect, useState } from "react";
import { BellRing, Factory, CalendarDays } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";

const num = (v) => (v === null || v === undefined || v === "" ? "" : Number(v));

/** 速览: the 采购报价 rows a buyer filed for one quote item. Read-only. */
export default function ProcurementFilingDialog({ quoteId, itemNumber, open, onClose }) {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !quoteId || !itemNumber) return;
    setLoading(true);
    api.get(`/quotes/${quoteId}/items/${encodeURIComponent(itemNumber)}/procurement-filings`)
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, quoteId, itemNumber]);

  const rows = data?.rows || [];
  const subs = data?.submissions || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[1000px]" data-testid="proc-filing-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <BellRing size={15} className="text-emerald-600" />
            {t("quote.filing.title")} · <span className="font-mono">{itemNumber}</span>
          </DialogTitle>
        </DialogHeader>

        {loading && <p className="text-xs text-muted-foreground py-6 text-center">{t("common.loading")}</p>}

        {!loading && rows.length === 0 && (
          <p className="text-xs text-muted-foreground py-4" data-testid="proc-filing-empty">
            {t("quote.filing.empty")}
          </p>
        )}

        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto border border-border rounded-md">
            <table className="w-full text-xs" data-testid="proc-filing-table">
              <thead className="bg-secondary/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5 text-left">{t("pq.quoteDate")}</th>
                  <th className="px-2 py-1.5 text-left">{t("pq.salesperson")}</th>
                  <th className="px-2 py-1.5 text-left">{t("pq.factory")}</th>
                  <th className="px-2 py-1.5 text-left">{t("pq.size")}</th>
                  <th className="px-2 py-1.5 text-right">{t("pq.price")}</th>
                  <th className="px-2 py-1.5 text-right">{t("pq.packingRate")}</th>
                  <th className="px-2 py-1.5 text-right">L×W×H (cm)</th>
                  <th className="px-2 py-1.5 text-right">{t("pq.grossWeight")}</th>
                  <th className="px-2 py-1.5 text-right">{t("pq.qtyHq")}</th>
                  <th className="px-2 py-1.5 text-left">{t("pq.remark")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-border/60" data-testid={`proc-filing-row-${r.id}`}>
                    <td className="px-2 py-1.5 font-mono">{r.quote_date || "-"}</td>
                    <td className="px-2 py-1.5">{r.salesperson || "-"}</td>
                    <td className="px-2 py-1.5">{r.factory || "-"}</td>
                    <td className="px-2 py-1.5">{r.size || "-"}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-bold">¥{num(r.price)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{num(r.packing_rate)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {[r.length, r.width, r.height].map((v) => num(v) || 0).join(" × ")}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{num(r.gross_weight)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{num(r.qty_hq)}</td>
                    <td className="px-2 py-1.5 max-w-[220px] truncate" title={r.remark || ""}>{r.remark || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && subs.length > 0 && (
          <div className="space-y-1.5 pt-1">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("quote.filing.log")}</p>
            <div className="flex flex-wrap gap-1.5">
              {subs.slice(0, 12).map((s, i) => (
                <Badge key={`${s.at}-${i}`} variant="outline" className="font-mono text-[10px] gap-1">
                  <CalendarDays size={9} /> {String(s.at).slice(0, 16).replace("T", " ")}
                  {s.buyer ? ` · ${s.buyer}` : ""}
                  {s.factory ? <><Factory size={9} /> {s.factory}</> : null}
                  {s.count > 1 ? ` ×${s.count}` : ""}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <p className="text-[11px] text-muted-foreground">{t("quote.filing.hint")}</p>
      </DialogContent>
    </Dialog>
  );
}
